import React from 'react';
import { withNavigate } from '@wfp-common/hooks/withNavigate';
import { NavigateHook } from '@wfp-common/hooks/useNavigate';

import { AsyncTaskStatus } from '../../utils/asyncTasks';
import { getBeneficiaryMappingTasks } from '../../apiClient';
import { ListViewProps } from './AsyncTasksPage';

interface TaskInfo {
    filename: string;
    status: string;
    progress?: number;
    id?: string;
    type?: string;
}

interface BatchInfo {
    batchId: string;
    tasks: TaskInfo[];
    createdAt: string;
    status: string;
    finishedAt?: string;
}

interface BeneficiaryMappingTasksListProps extends ListViewProps {
    navigate: NavigateHook;
}

const BeneficiaryMappingTasksList: React.FC<BeneficiaryMappingTasksListProps> = (props) => {
    const [batches, setBatches] = React.useState<BatchInfo[]>([]);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [error, setError] = React.useState<string | null>(null);

    const fetchMappingTasks = React.useCallback(async () => {
        try {
            setLoading(true);
            const response = await getBeneficiaryMappingTasks();

            // Map to add id and type from the data prop (AsyncTask objects)
            const mapTasksWithAsyncTaskData = (tasks: TaskInfo[]): TaskInfo[] => {
                return tasks.map((task) => {
                    // Find matching AsyncTask by filename
                    const matchingAsyncTask = props.data.find((asyncTask) => asyncTask.fileName === task.filename);

                    if (matchingAsyncTask) {
                        return {
                            ...task,
                            id: matchingAsyncTask.id,
                            type: matchingAsyncTask.type,
                        };
                    }

                    return task;
                });
            };

            if (response.batches) {
                // Map each batch's tasks with AsyncTask data
                const mappedBatches = response.batches.map((batch) => ({
                    ...batch,
                    tasks: mapTasksWithAsyncTaskData(batch.tasks),
                    // Ensure status is properly capitalized for display
                    status: batch.status ? batch.status.toLowerCase() : 'active',
                }));

                setBatches(mappedBatches);
            } else {
                // If no batches are returned, create a single batch with all tasks
                if (response.tasks && response.tasks.length > 0) {
                    setBatches([
                        {
                            batchId: 'default',
                            tasks: mapTasksWithAsyncTaskData(response.tasks),
                            createdAt: new Date().toISOString(),
                            status: 'active', // Default status for manually created batch
                        },
                    ]);
                } else {
                    setBatches([]);
                }
            }
            setError(null);
        } catch (err) {
            setError('Failed to load mapping tasks');
        } finally {
            setLoading(false);
        }
    }, [props.data]);

    React.useEffect(() => {
        // Initial fetch
        fetchMappingTasks();

        // Set up interval to fetch data every 5 seconds
        const intervalId = setInterval(() => {
            fetchMappingTasks();
        }, 5000);

        // Clean up interval on component unmount
        return () => clearInterval(intervalId);
    }, [fetchMappingTasks]);

    if (loading && batches.length === 0) {
        return <div className="wfp-loading wfp-loading--lg" style={{ margin: '20px auto', display: 'block' }} />;
    }

    if (error) {
        return (
            <div className="wfp-alert wfp-alert--error" style={{ margin: '20px 0' }}>
                <p>{error}</p>
            </div>
        );
    }

    if (batches.length === 0) {
        return (
            <div
                style={{
                    padding: '20px',
                    textAlign: 'center',
                    border: '1px dashed #ddd',
                    borderRadius: '4px',
                    margin: '20px 0',
                }}
            >
                <div style={{ color: '#666', marginBottom: '10px', fontSize: '16px' }}>
                    <i className="fa fa-info-circle" style={{ marginRight: '5px' }} />
                    No mapping tasks found.
                </div>
                <p style={{ color: '#888', fontSize: '14px', margin: '0' }}>
                    Upload a file to start the mapping process.
                </p>
            </div>
        );
    }

    // Sort batches by createdAt (newest first)
    const sortedBatches = [...batches].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    return (
        <div style={{ margin: '20px 0' }}>
            {sortedBatches.map((batch) => (
                <div
                    key={batch.batchId}
                    style={{
                        marginBottom: '20px',
                        border: '1px solid #eee',
                        borderRadius: '4px',
                        overflow: 'hidden',
                    }}
                >
                    <div
                        style={{
                            backgroundColor: '#f5f5f5',
                            padding: '10px 15px',
                            borderBottom: '1px solid #eee',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <div>
                            <h5 style={{ margin: 0, fontWeight: 'bold' }}>Batch ID: {batch.batchId}</h5>
                            <div
                                style={{
                                    display: 'inline-block',
                                    marginTop: '5px',
                                    padding: '2px 8px',
                                    borderRadius: '3px',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    textTransform: 'uppercase',
                                    backgroundColor:
                                        batch.status === 'active'
                                            ? '#0071bc'
                                            : batch.status === 'finished'
                                              ? '#28a745'
                                              : batch.status === 'finishedWithWarnings'
                                                ? '#ffc107'
                                                : batch.status === 'cancelled'
                                                  ? '#dc3545'
                                                  : '#6c757d',
                                    color: '#fff',
                                }}
                            >
                                {batch.status}
                            </div>
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                            <div>Created: {new Date(batch.createdAt).toLocaleString()}</div>
                            {(batch.status === 'finished' || batch.status === 'cancelled') && batch.finishedAt && (
                                <div style={{ marginTop: '4px' }}>
                                    Finished: {new Date(batch.finishedAt).toLocaleString()}
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{ padding: '0' }}>
                        {batch.tasks.map((task, taskIndex) => (
                            <div
                                className={task.id && task.type ? 'cursor-pointer' : ''}
                                key={`${batch.batchId}-${taskIndex}`}
                                onClick={() => {
                                    if (task.id && task.type) {
                                        props.navigate(`/tasks/${task.type}/${task.id}`);
                                    }
                                }}
                                style={{
                                    padding: '12px 15px',
                                    borderBottom: taskIndex < batch.tasks.length - 1 ? '1px solid #eee' : 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: '#fff',
                                    cursor: task.id && task.type ? 'pointer' : 'default',
                                    transition: 'background-color 0.2s',
                                }}
                            >
                                <div
                                    style={{
                                        marginRight: '15px',
                                        color: '#0071bc',
                                        fontSize: '18px',
                                    }}
                                >
                                    <i aria-hidden="true" className="fa fa-file-text-o" />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 'bold' }}>{task.filename}</div>
                                    {task.progress !== undefined && task.progress < 1 ? (
                                        <div style={{ marginTop: '8px' }}>
                                            <div
                                                style={{
                                                    height: '6px',
                                                    backgroundColor: '#e9ecef',
                                                    borderRadius: '3px',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: `${task.progress * 100}%`,
                                                        height: '100%',
                                                        backgroundColor: '#0071bc',
                                                        borderRadius: '3px',
                                                    }}
                                                />
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                                {Math.round(task.progress * 100)}% complete
                                            </div>
                                        </div>
                                    ) : (
                                        <div
                                            style={{
                                                fontSize: '12px',
                                                color:
                                                    task.status === AsyncTaskStatus.finished
                                                        ? '#28a745'
                                                        : task.status === AsyncTaskStatus.finishedWithWarnings
                                                          ? '#ffc107'
                                                          : task.status === AsyncTaskStatus.failed
                                                            ? '#dc3545'
                                                            : '#666',
                                                marginTop: '4px',
                                            }}
                                        >
                                            {task.status}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default withNavigate(BeneficiaryMappingTasksList, 'BeneficiaryMappingTasksList');
